import React, { useState, useEffect, useRef } from 'react';
import useSWR from 'swr';
import { fetchLoyaltyHistorySWR, getLoyaltyHistoryUrl } from './actions';
import './activity-panel.css';
import LottieAnimation from 'components/Lottie';
import { 
  LOYALTY_ACTIVITY_ACTIONS, 
  LOYALTY_ACTIVITY_DISPLAY_TEXT
} from 'helpers/enums';

// Import SVG icons
import { ReactComponent as RestaurantIcon } from '../TableBookingMobile/img/silverware.svg';
import { ReactComponent as RefundIcon } from '../../assets/images/arrow-counterclockwise.svg';
import { ReactComponent as ReferralIcon } from '../../assets/images/referral-icon.svg';
import { ReactComponent as CancelIcon } from '../../assets/images/icon-close.svg';
import { ReactComponent as RedemptionIcon } from '../../assets/images/gift-box.svg';
import { ReactComponent as WelcomeIcon } from '../../assets/images/star-yellow.svg';

const ActivityPanel = ({
  isOpen,
  onClose,
  currentPoints,
  themeColors,
  userDetails,
  storeConfig,
  pointsTerminology = 'points',
  settingId,
  height,
}) => {
  // State to track loading more items
  const [loadingMore, setLoadingMore] = useState(false);
  // State to store all loaded history items
  const [historyItems, setHistoryItems] = useState([]);
  // State to track if there are more items to load
  const [hasMore, setHasMore] = useState(true);
  // State to track the current offset for pagination
  const [currentOffset, setCurrentOffset] = useState(0);
  // State to track if pagination has failed (to prevent infinite retries)
  const [paginationFailed, setPaginationFailed] = useState(false);

  // Reference to the container for scroll detection
  const containerRef = useRef(null);
  // Reference to the activity panel for click outside detection
  const panelRef = useRef(null);

  // Get necessary IDs for API calls
  const userId = userDetails?._id || '';
  const storeId = storeConfig?.storeId || '';
  const restaurantId = storeConfig?.restaurantId || '';

  // Initial fetch with SWR
  const {
    data: initialHistory,
    error,
    isLoading,
  } = useSWR(
    // Only fetch if we have the required IDs and the panel is open
    isOpen && userId && storeId && restaurantId
      ? getLoyaltyHistoryUrl(userId, storeId, restaurantId, settingId, 0, 10)
      : null,
    fetchLoyaltyHistorySWR,
    {
      revalidateOnFocus: false,
      dedupingInterval: 0, // Always fetch fresh data when key changes
      revalidateOnMount: true, // Always fetch when component mounts
    }
  );

  // Handle click outside to close panel
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if the click is on the activity button or its children (span.activity-text or caret icons)
      const isActivityButtonClick = event.target.closest('.activity-button');

      // Only close if click is outside the panel AND not on the activity button
      if (
        panelRef.current &&
        !panelRef.current.contains(event.target) &&
        !isActivityButtonClick &&
        isOpen
      ) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Update history items when initial data loads
  useEffect(() => {
    const historyArray = initialHistory?.history || [];
    if (initialHistory && historyArray.length > 0) {
      setHistoryItems(historyArray);
      setCurrentOffset(historyArray.length); // Set offset to number of items loaded
      
      // Determine if there are more items based on response size
      // Since API consistently returns 9 items on initial load when requesting 10,
      // and we know there are more items available, enable pagination for 9+ items
      const hasMoreItems = historyArray.length >= 9;
      setHasMore(hasMoreItems);

    } else if (initialHistory && historyArray.length === 0) {
      setHasMore(false);
      setCurrentOffset(0);
    }
  }, [initialHistory]);

  // Reset pagination state when panel closes
  useEffect(() => {
    if (!isOpen) {
      // Reset state when panel closes to ensure fresh start next time
      setHistoryItems([]);
      setHasMore(true);
      setCurrentOffset(0);
      setLoadingMore(false);
      setPaginationFailed(false);
    }
  }, [isOpen]);

  // Function to load more history items
  const loadMoreHistory = async () => {
    
    if (!hasMore || loadingMore || paginationFailed || !userId || !storeId || !restaurantId || !settingId) {
      return;
    }

    setLoadingMore(true);

    try {
      // Use offset-based pagination - load next 10 items starting from currentOffset
      const url = getLoyaltyHistoryUrl(
        userId,
        storeId,
        restaurantId,
        settingId,
        currentOffset, // offset for pagination
        10 // limit
      );

      const moreHistoryResponse = await fetchLoyaltyHistorySWR(url);
      const moreHistory = moreHistoryResponse?.history || [];

      if (moreHistory && moreHistory.length > 0) {
        setHistoryItems((prev) => [...prev, ...moreHistory]);
        
        // Update offset for next pagination request
        setCurrentOffset(prevOffset => prevOffset + moreHistory.length);

        // Only continue if we received exactly 10 items (full page)
        // If less than 10, we've reached the end
        setHasMore(moreHistory.length === 10);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      // Stop further pagination attempts when API fails
      setHasMore(false);
      setPaginationFailed(true);
    } finally {
      setLoadingMore(false);
    }
  };

  // Handle scroll event for infinite scrolling
  const handleScroll = () => {
    const container = containerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;

    // Load more when user scrolls to bottom (with a threshold)
    if (
      scrollHeight - scrollTop - clientHeight < 10 &&
      hasMore &&
      !loadingMore
    ) {
      loadMoreHistory();
    }
  };

  // Format date from timestamp - now returns day Month year format
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';

    const date = new Date(timestamp);
    return `${date.getDate()} ${date.toLocaleDateString('en-US', {
      month: 'long',
    })} ${date.getFullYear()}`;
  };

  // If panel is not open, don't render
  if (!isOpen) return null;

  // Capitalize points terminology
  const capitalizedTerminology =
    pointsTerminology.charAt(0).toUpperCase() + pointsTerminology.slice(1);

  // Calculate panel height based on 5 items at 50px each, plus header space and gaps between items
  // Header height (h4 + margin) is approximately 50px
  // Each item is 50px and we need to add 7px gap between each (4 gaps for 5 items)
  const panelHeight = height || '350px'; // 250px for 5 items + 28px for 4 gaps + 50px for header

  // Get display text based on action type
  const getDisplayText = (item) => {
    if (!item) return 'Activity';

    // Special cases that show order IDs
    if (item.action === LOYALTY_ACTIVITY_ACTIONS.POINTS_EARNED) {
      if (item.sourceId) {
        return `Order #${item.sourceId.slice(-4)} completed`;
      }
      // Fallback if no sourceId
      return `Delicious order completed`;
    }

    if (item.action === LOYALTY_ACTIVITY_ACTIONS.ORDER_CANCELLED) {
      if (item.sourceId) {
        return `Order #${item.sourceId.slice(-4)} was cancelled`;
      }
      return 'Order cancelled - points removed';
    }

    if (item.action === LOYALTY_ACTIVITY_ACTIONS.ORDER_REFUNDED) {
      if (item.sourceId) {
        return `Order #${item.sourceId.slice(-4)} refunded`;
      }
      return 'Order refunded - points returned';
    }

    // Check if we have a predefined display text for other actions
    if (LOYALTY_ACTIVITY_DISPLAY_TEXT[item.action]) {
      return LOYALTY_ACTIVITY_DISPLAY_TEXT[item.action];
    }

    // Default fallback
    return item.description || 'Activity';
  };

  const isPointsDeduction = (item) => {
    if (!item) return false;

    const action = String(item.action || '').trim().toUpperCase();

    switch (action) {
      case LOYALTY_ACTIVITY_ACTIONS.POINTS_REDEEMED:
      case LOYALTY_ACTIVITY_ACTIONS.POINTS_EXPIRED:
      case LOYALTY_ACTIVITY_ACTIONS.REDEMPTION:
      case LOYALTY_ACTIVITY_ACTIONS.ORDER_CANCELLED:
      case LOYALTY_ACTIVITY_ACTIONS.ORDER_REFUNDED:
        return true;

      default:
        break;
    }

    const activityText = [
      action,
      getDisplayText(item),
      item.description,
      item.type,
      item.transactionType,
      item.activityType,
      item.reason,
    ]
      .filter(Boolean)
      .join(' ')
      .toUpperCase();

    return (
      Number(item.points) < 0 ||
      activityText.includes('EXPIRE') ||
      activityText.includes('EXPIRY') ||
      activityText.includes('DEDUCT')
    );
  };

  // Get background color based on point movement
  const getPointsBackgroundColor = (item) => {
    if (isPointsDeduction(item)) {
      return 'rgb(238, 2, 2)';
    }

    const action = item?.action;
    switch (action) {
      case LOYALTY_ACTIVITY_ACTIONS.SIGNUP_BONUS:
      case LOYALTY_ACTIVITY_ACTIONS.REFERRAL_BONUS:
      case LOYALTY_ACTIVITY_ACTIONS.POINTS_EARNED:
        return 'rgba(74, 187, 31, 0.77)'; // Green for earned points/bonuses

      default:
        return 'rgba(74, 187, 31, 0.77)';
    }
  };

  const getPointsBackgroundColorDark = (item) => {
    return isPointsDeduction(item)
      ? 'rgba(238, 2, 2, 0.9)'
      : 'rgba(74, 187, 31, 0.9)';
  };

  const getActivityIcon = (action) => {
    switch (action) {
      case LOYALTY_ACTIVITY_ACTIONS.POINTS_EARNED:
        return { 
          icon: <RestaurantIcon />, 
          class: 'icon-restaurant', 
          ariaLabel: 'Restaurant order' 
        };
      case LOYALTY_ACTIVITY_ACTIONS.SIGNUP_BONUS:
        return { 
          icon: <WelcomeIcon />, 
          class: 'icon-welcome', 
          ariaLabel: 'Welcome bonus' 
        };
      case LOYALTY_ACTIVITY_ACTIONS.REFERRAL_BONUS:
        return { 
          icon: <ReferralIcon />, 
          class: 'icon-referral', 
          ariaLabel: 'Referral bonus' 
        };
      case LOYALTY_ACTIVITY_ACTIONS.POINTS_REDEEMED:
      case LOYALTY_ACTIVITY_ACTIONS.POINTS_EXPIRED:
      case LOYALTY_ACTIVITY_ACTIONS.REDEMPTION:
        return { 
          icon: <RedemptionIcon />, 
          class: 'icon-redemption', 
          ariaLabel: 'Points redeemed' 
        };
      case LOYALTY_ACTIVITY_ACTIONS.ORDER_CANCELLED:
        return { 
          icon: <CancelIcon />, 
          class: 'icon-cancelled', 
          ariaLabel: 'Order cancelled' 
        };
      case LOYALTY_ACTIVITY_ACTIONS.ORDER_REFUNDED:
        return { 
          icon: <RefundIcon />, 
          class: 'icon-refund', 
          ariaLabel: 'Order refunded' 
        };
      default:
        return { 
          icon: <RedemptionIcon />, 
          class: 'icon-default', 
          ariaLabel: 'Activity' 
        };
    }
  };

  const getArrowDirection = (item) => {
    if (isPointsDeduction(item)) {
      return '↓'; // Down arrow for losses
    }

    const action = item?.action;
    switch (action) {
      case LOYALTY_ACTIVITY_ACTIONS.POINTS_EARNED:
      case LOYALTY_ACTIVITY_ACTIONS.SIGNUP_BONUS:
      case LOYALTY_ACTIVITY_ACTIONS.REFERRAL_BONUS:
        return '↑'; // Up arrow for gains

      default:
        return '↑'; // Default to up arrow
    }
  };


  return (
    <div
      className='activity-panel'
      style={{ height: historyItems.length === 0 ? '200px' : panelHeight }}
      ref={panelRef}
    >
      <div className='activity-history-section'>
        <h4>Balance Activity</h4>

        <div
          className='activity-list-container'
          ref={containerRef}
          onScroll={handleScroll}
        >
          {isLoading ? (
            <div className='activity-loading'>Loading history...</div>
          ) : error ? (
            <div className='activity-error'>Error loading history</div>
          ) : historyItems.length === 0 ? (
            <div className='activity-empty'>No activity history</div>
          ) : (
            <ul className='activity-list'>
              {historyItems.map((item, index) => (
                <li key={`${item.date}-${index}`} className='activity-item'>
                  <div className='activity-item-left'>
                    <div 
                      className={`activity-icon ${getActivityIcon(item.action).class}`}
                      aria-label={getActivityIcon(item.action).ariaLabel}
                      role="img"
                    >
                      {getActivityIcon(item.action).icon}
                    </div>
                    <div className='activity-details'>
                      <div className='activity-item-order'>
                        {getDisplayText(item)}
                      </div>
                      <div className='activity-item-date'>
                        {formatDate(item.date)}
                      </div>
                    </div>
                  </div>
                  <div className='activity-item-right'>
                    <div
                      className='activity-item-points-pill'
                      style={{
                        '--pill-color': getPointsBackgroundColor(item),
                        '--pill-color-dark': getPointsBackgroundColorDark(item),
                        backgroundColor: getPointsBackgroundColor(item)
                      }}
                    >
                      <span className='activity-item-points-value'>
                        {Math.abs(item.points).toLocaleString()}{' '}
                        {capitalizedTerminology}
                      </span>
                      <span className='activity-item-points-arrow'>
                        {getArrowDirection(item)}
                      </span>
                    </div>
                  </div>
                </li>
              ))}

              {loadingMore && (
                <div className='activity-loading-more'>
                  <LottieAnimation
                    statusText='Loading more...'
                    loop={true}
                    size='small'
                    backgroundColor={themeColors?.backgroundColor}
                  />
                </div>
              )}

              {!hasMore && historyItems.length > 0 && (
                <li className='activity-end'>End of history</li>
              )}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default ActivityPanel;
